import { OBJECTION_HANDLED_SCORE_THRESHOLD } from '../constants/analysis';
import {
  OBJECTION_CODES_VERSION,
  isObjectionCode,
  resolveSecondaryObjectionCode,
} from '../voc/objection-codes';

export interface ReconcileObjectionHandlingResult {
  output: Record<string, unknown>;
  applied: boolean;
  objectionCount: number;
  score: number | null;
  /**
   * How many items carried a code outside the set and lost it. The strict
   * schema cannot produce one, so a non-zero value is a broken provider
   * contract, not an unfit code set — the caller must log it, or the breakage
   * reads as a growing share of unresolved objections.
   */
  invalidCodeCount: number;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function realNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function clampScore(value: number): number {
  return Math.min(100, Math.max(0, value));
}

/**
 * Read-side compatibility rule for an objection item.
 *
 * A finite score is authoritative for current records. Only a genuinely
 * absent score falls back to the historical boolean; malformed score values
 * fail closed instead of reviving a contradictory `handled=true` flag.
 */
/**
 * The handling score of an objection item as the product reads it: a finite
 * number clamped to 0–100, else `null`. The one rule for both the verdict
 * (`isObjectionHandled`) and the numbers shown next to it (#846) — a copy of
 * it would let a score on screen disagree with the "handled" it sits beside.
 */
export function readObjectionScore(item: unknown): number | null {
  if (!isObject(item)) return null;
  const score = realNumber(item.score);
  return score === null ? null : clampScore(score);
}

export function isObjectionHandled(item: unknown): boolean {
  if (!isObject(item)) return false;
  const score = readObjectionScore(item);
  if (score !== null) return score >= OBJECTION_HANDLED_SCORE_THRESHOLD;
  return !Object.prototype.hasOwnProperty.call(item, 'score') && item.handled === true;
}

/**
 * Keep `code` / `secondaryCode` inside the canonical set — for items that carry
 * them at all.
 *
 * An ABSENT code stays absent. This function also re-finalizes historical
 * objects, and an objection stored before codes existed is resolved through the
 * label map on read (`resolveObjectionCode`).
 *
 * A PRESENT code outside the set is DROPPED, not rewritten to `other`. `other`
 * is a verdict ("a real objection that fits no code") and its share measures
 * the fitness of the set; an unreadable code is ignorance, and ignorance must
 * stay resolvable: with the field gone the read side falls back to the label
 * map, exactly as the resolver's own contract promises. Rewriting to `other`
 * would shadow the map and merge two measures the set keeps apart on purpose.
 * The drop is counted so the caller can log it.
 *
 * A secondary code that is invalid or repeats the primary becomes null; a
 * secondary without a usable primary goes with it.
 */
function reconcileObjectionCodes(item: Record<string, unknown>): {
  item: Record<string, unknown>;
  applied: boolean;
  invalid: boolean;
} {
  const hasCode = Object.prototype.hasOwnProperty.call(item, 'code');
  const hasSecondary = Object.prototype.hasOwnProperty.call(item, 'secondaryCode');
  if (!hasCode && !hasSecondary) return { item, applied: false, invalid: false };

  if (!isObjectionCode(item.code)) {
    const rest = { ...item };
    delete rest.code;
    delete rest.secondaryCode;
    return { item: rest, applied: true, invalid: hasCode };
  }
  if (!hasSecondary) return { item, applied: false, invalid: false };

  const secondaryCode = resolveSecondaryObjectionCode(item.secondaryCode, item.code);
  if (item.secondaryCode === secondaryCode)
    return { item, applied: false, invalid: false };
  // A secondary repeating the primary is a semantic slip the schema allows; a
  // secondary outside the set is a broken contract, same as an invalid primary.
  const invalidSecondary =
    item.secondaryCode !== null && !isObjectionCode(item.secondaryCode);
  return { item: { ...item, secondaryCode }, applied: true, invalid: invalidSecondary };
}

/**
 * Make the per-item score the only semantic source of truth.
 *
 * New strict outputs always carry `score`. The `handled` fallback exists only
 * so an explicitly re-finalized historical object can be upgraded without
 * turning every legacy handled objection into an unhandled one.
 */
export function reconcileObjectionHandling(
  output: Record<string, unknown>
): ReconcileObjectionHandlingResult {
  const objectionHandling = output.objectionHandling;
  if (!isObject(objectionHandling) || !Array.isArray(objectionHandling.objections)) {
    return {
      output,
      applied: false,
      objectionCount: 0,
      score: null,
      invalidCodeCount: 0,
    };
  }

  let applied = false;
  let invalidCodeCount = 0;
  const storedCodesVersion = objectionHandling.codesVersion;
  const foreignCodesVersion =
    typeof storedCodesVersion === 'string' &&
    storedCodesVersion !== OBJECTION_CODES_VERSION;
  const objections = objectionHandling.objections.flatMap((item) => {
    // Strict new output cannot contain malformed items. If an old/bypass
    // payload does, drop it rather than counting an uninspectable entry as a
    // scored zero and then persisting it in the canonical contract.
    if (!isObject(item)) {
      applied = true;
      return [];
    }

    const rawScore = realNumber(item.score);
    const hasScore = Object.prototype.hasOwnProperty.call(item, 'score');
    const score =
      rawScore === null
        ? !hasScore && item.handled === true
          ? 100
          : 0
        : clampScore(rawScore);
    const handled = score >= OBJECTION_HANDLED_SCORE_THRESHOLD;
    if (item.score !== score || item.handled !== handled) applied = true;
    // Codes of a FOREIGN set version are left exactly as stored: validating
    // them against today's set would drop every code a later revision renamed.
    const codes = foreignCodesVersion
      ? { item, applied: false, invalid: false }
      : reconcileObjectionCodes(item);
    if (codes.applied) applied = true;
    if (codes.invalid) invalidCodeCount += 1;
    return [{ ...codes.item, score, handled }];
  });

  // An input array that survived reconciliation empty did not necessarily
  // describe a call without objections: every entry may have been dropped as
  // unreadable above. Handing that payload the vacuous 100 would score the most
  // broken input we ever accept as perfect objection handling, and the metric
  // flows straight into the canonical quality mean. Fall back to the aggregate
  // the payload already carried, and — when that is unusable too — fail closed
  // at 0, the same way a single unreadable item does.
  const droppedEveryObjection =
    objections.length === 0 && objectionHandling.objections.length > 0;
  const priorScore = realNumber(objectionHandling.score);
  const score =
    objections.length === 0
      ? droppedEveryObjection
        ? priorScore === null
          ? 0
          : clampScore(priorScore)
        : 100
      : Math.round(
          (objections.reduce((sum, item) => sum + (realNumber(item.score) ?? 0), 0) /
            objections.length) *
            100
        ) / 100;
  if (objectionHandling.score !== score) applied = true;

  // Stamp the set version next to the codes it describes. The set is versioned
  // (`objection_codes_v2` may split or rename a code), and a stored code with no
  // version cannot be told apart from a code of the next set. System-stamped,
  // like `handled`: the model never sees or returns it.
  const stampsVersion =
    storedCodesVersion === undefined &&
    objections.some((item) => Object.prototype.hasOwnProperty.call(item, 'code'));
  if (stampsVersion) applied = true;

  if (!applied) {
    return {
      output,
      applied: false,
      objectionCount: objections.length,
      score,
      invalidCodeCount,
    };
  }

  return {
    output: {
      ...output,
      objectionHandling: {
        ...objectionHandling,
        score,
        objections,
        ...(stampsVersion && { codesVersion: OBJECTION_CODES_VERSION }),
      },
    },
    applied: true,
    objectionCount: objections.length,
    score,
    invalidCodeCount,
  };
}
